<template>
  <div class="rebate-rate-management">
    <div class="page-header">
      <div class="header-left">
        <h2>返点配置管理</h2>
        <p class="header-desc">按省市配置返点比例、税率及邀请奖励。</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i> 新增返点配置
        </el-button>
      </div>
    </div>

    <div class="config-container">
      <!-- 搜索筛选 -->
      <div class="search-bar">
        <span class="search-label">省份：</span>
        <el-select
          v-model="filters.provinceCode"
          placeholder="请选择/搜索省份"
          clearable
          filterable
          class="search-select"
          @change="handleProvinceFilterChange"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="item in provinceList"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
        <span class="search-label">城市：</span>
        <el-select
          v-model="filters.cityCode"
          placeholder="请选择/搜索城市"
          clearable
          filterable
          class="search-select"
          @change="handleSearch"
          :disabled="!filters.provinceCode"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="item in cityList"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
      </div>

      <!-- 表格 -->
      <el-table :data="list" style="width: 100%" v-loading="loading" border>
        <el-table-column label="序号" width="60" type="index" />
        <el-table-column label="省份" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.provinceName }}
          </template>
        </el-table-column>
        <el-table-column label="城市" width="140">
          <template slot-scope="scope">
            {{ scope.row.cityName }}
          </template>
        </el-table-column>
        <el-table-column label="返点比例" width="110">
          <template slot-scope="scope"> {{ scope.row.rebateRate }}% </template>
        </el-table-column>
        <el-table-column label="税率" width="100">
          <template slot-scope="scope"> {{ scope.row.taxRate }}% </template>
        </el-table-column>
        <el-table-column label="邀请注册奖励" width="140">
          <template slot-scope="scope">
            {{ scope.row.inviteRegisterReward }}
          </template>
        </el-table-column>
        <el-table-column label="邀请下单奖励" width="140">
          <template slot-scope="scope">
            {{ scope.row.inviteOrderReward }}
          </template>
        </el-table-column>
        <el-table-column label="邀请绑定奖励" width="140">
          <template slot-scope="scope">
            {{ scope.row.bindReward }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.createTime | formatDate }}
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.updateTime | formatDate }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleView(scope.row)"
              >查看</el-button
            >
            <el-button type="text" @click="handleEdit(scope.row)"
              >编辑</el-button
            >
            <el-button type="text" @click="handleDelete(scope.row)"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <span class="pagination-info">
          显示第 {{ (currentPage - 1) * pageSize + 1 }} 至
          {{ Math.min(currentPage * pageSize, total) }} 条，共
          {{ total }} 条记录
        </span>
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </div>

    <!-- 弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="560px"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <el-form
        ref="rebateForm"
        :model="rebateForm"
        :rules="rebateRules"
        label-width="120px"
      >
        <el-form-item label="省份" prop="provinceCode">
          <el-select
            v-model="rebateForm.provinceCode"
            placeholder="请选择/搜索省份"
            style="width: 100%"
            :disabled="isViewMode"
            filterable
            @change="handleFormProvinceChange"
          >
            <el-option
              v-for="item in provinceList"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="城市" prop="cityCode">
          <el-select
            v-model="rebateForm.cityCode"
            placeholder="请选择/搜索城市"
            style="width: 100%"
            :disabled="!rebateForm.provinceCode || isViewMode"
            filterable
          >
            <el-option
              v-for="item in formCityList"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="返点比例" prop="rebateRate">
          <el-input
            v-model="rebateForm.rebateRate"
            placeholder="请输入 0-100"
            :disabled="isViewMode"
          >
            <template slot="append">%</template>
          </el-input>
        </el-form-item>
        <el-form-item label="税率" prop="taxRate">
          <el-input
            v-model="rebateForm.taxRate"
            placeholder="请输入 0-100"
            :disabled="isViewMode"
          >
            <template slot="append">%</template>
          </el-input>
        </el-form-item>
        <el-form-item label="邀请注册奖励" prop="inviteRegisterReward">
          <el-input
            v-model="rebateForm.inviteRegisterReward"
            placeholder="请输入 0-100000"
            :disabled="isViewMode"
          >
            <template slot="append">元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="邀请下单奖励" prop="inviteOrderReward">
          <el-input
            v-model="rebateForm.inviteOrderReward"
            placeholder="请输入 0-100000"
            :disabled="isViewMode"
          >
            <template slot="append">元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="邀请绑定奖励" prop="bindReward">
          <el-input
            v-model="rebateForm.bindReward"
            placeholder="请输入 0-100000"
            :disabled="isViewMode"
          >
            <template slot="append">元</template>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{
          isViewMode ? '关闭' : '取消'
        }}</el-button>
        <el-button
          v-if="!isViewMode"
          type="primary"
          @click="handleSubmit"
          :loading="submitLoading"
          >确认保存</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 百分比校验：0-100，支持小数
const validatePercent = (rule, value, callback) => {
  if (value === '' || value === null || value === undefined) {
    callback(new Error('请输入数值'))
    return
  }
  const num = Number(value)
  if (isNaN(num)) {
    callback(new Error('请输入有效数字'))
  } else if (num < 0 || num > 100) {
    callback(new Error('取值范围 0 - 100'))
  } else {
    callback()
  }
}
// 正整数校验：0-100000
const validatePositive = (rule, value, callback) => {
  if (value === '' || value === null || value === undefined) {
    callback(new Error('请输入数值'))
    return
  }
  const num = Number(value)
  if (isNaN(num)) {
    callback(new Error('请输入有效数字'))
  } else if (!Number.isInteger(num)) {
    callback(new Error('请输入整数'))
  } else if (num < 0 || num > 100000) {
    callback(new Error('取值范围 0 - 100000'))
  } else {
    callback()
  }
}

export default {
  name: 'RebateRateManagement',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: {
        provinceCode: '',
        cityCode: ''
      },
      // 弹窗
      dialogVisible: false,
      isViewMode: false,
      isEditMode: false,
      submitLoading: false,
      rebateForm: {
        id: null,
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        rebateRate: '',
        taxRate: '',
        inviteRegisterReward: '',
        inviteOrderReward: '',
        bindReward: ''
      },
      rebateRules: {
        provinceCode: [
          { required: true, message: '请选择省份', trigger: 'change' }
        ],
        cityCode: [
          { required: true, message: '请选择城市', trigger: 'change' }
        ],
        rebateRate: [
          { required: true, validator: validatePercent, trigger: 'blur' }
        ],
        taxRate: [
          { required: true, validator: validatePercent, trigger: 'blur' }
        ],
        inviteRegisterReward: [
          { required: true, validator: validatePositive, trigger: 'blur' }
        ],
        inviteOrderReward: [
          { required: true, validator: validatePositive, trigger: 'blur' }
        ],
        bindReward: [
          { required: true, validator: validatePositive, trigger: 'blur' }
        ]
      },
      // 下拉选项
      provinceList: [],
      cityList: [],
      formCityList: []
    }
  },
  computed: {
    dialogTitle() {
      if (this.isViewMode) return '查看返点配置详情'
      if (this.isEditMode) return '编辑返点配置'
      return '新增返点配置'
    }
  },
  mounted() {
    this.fetchList()
    this.loadProvinceList()
  },
  methods: {
    // ===== 列表相关 =====
    async fetchList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }
        if (this.filters.provinceCode) {
          params.provinceCode = this.filters.provinceCode
        }
        if (this.filters.cityCode) {
          params.cityCode = this.filters.cityCode
        }
        const res = await this.$http.get('rebateRate/getRebateRateList.do', {
          params
        })
        if (res.code === '0' || res.code === 0) {
          this.list = res.result || []
          this.total = res.total || 0
        } else {
          this.$message.error(res.message || '获取区域返点列表失败')
        }
      } catch (error) {
        console.error('获取区域返点列表失败:', error)
        this.$message.error('网络请求异常')
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchList()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchList()
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    handleProvinceFilterChange(provinceCode) {
      this.filters.cityCode = ''
      if (provinceCode) {
        this.loadCityList(provinceCode, 'filter')
      } else {
        this.cityList = []
      }
      this.handleSearch()
    },

    // ===== 省市数据加载 =====
    async loadProvinceList() {
      try {
        const res = await this.$http.get('/region/getAllProvinces.do')
        if (res.code === '0' || res.code === 0) {
          this.provinceList = res.result || []
        }
      } catch (error) {
        console.error('加载省份列表失败:', error)
      }
    },
    async loadCityList(provinceCode, target = 'filter') {
      try {
        const res = await this.$http.get('/region/getCitiesByProvince.do', {
          params: { provinceCode }
        })
        if (res.code === '0' || res.code === 0) {
          if (target === 'filter') {
            this.cityList = res.result || []
          } else {
            this.formCityList = res.result || []
          }
        }
      } catch (error) {
        console.error('加载城市列表失败:', error)
      }
    },

    // ===== 表单相关 =====
    resetForm() {
      this.rebateForm = {
        id: null,
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        rebateRate: '',
        taxRate: '',
        inviteRegisterReward: '',
        inviteOrderReward: '',
        bindReward: ''
      }
      this.formCityList = []
    },
    handleCreate() {
      this.isViewMode = false
      this.isEditMode = false
      this.resetForm()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.rebateForm && this.$refs.rebateForm.clearValidate()
      })
    },
    async handleEdit(row) {
      this.isViewMode = false
      this.isEditMode = true
      await this.loadDetail(row.id)
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.rebateForm && this.$refs.rebateForm.clearValidate()
      })
    },
    async handleView(row) {
      this.isViewMode = true
      this.isEditMode = false
      await this.loadDetail(row.id)
      this.dialogVisible = true
    },
    async loadDetail(id) {
      try {
        const res = await this.$http.get('rebateRate/getRebateRateDetail.do', {
          params: { id }
        })
        if (res.code === '0' || res.code === 0) {
          const data = res.result || {}
          this.rebateForm = {
            id: data.id,
            provinceCode: data.provinceCode || '',
            provinceName: data.provinceName || '',
            cityCode: data.cityCode || '',
            cityName: data.cityName || '',
            rebateRate: data.rebateRate !== undefined ? data.rebateRate : '',
            taxRate: data.taxRate !== undefined ? data.taxRate : '',
            inviteRegisterReward:
              data.inviteRegisterReward !== undefined
                ? data.inviteRegisterReward
                : '',
            inviteOrderReward:
              data.inviteOrderReward !== undefined
                ? data.inviteOrderReward
                : '',
            bindReward: data.bindReward !== undefined ? data.bindReward : ''
          }
          if (data.provinceCode) {
            await this.loadCityList(data.provinceCode, 'form')
          }
        } else {
          this.$message.error(res.message || '获取返点配置详情失败')
        }
      } catch (error) {
        console.error('获取返点配置详情失败:', error)
        this.$message.error('网络请求异常')
      }
    },
    handleFormProvinceChange(provinceCode) {
      this.rebateForm.cityCode = ''
      this.rebateForm.cityName = ''
      this.rebateForm.provinceName = ''
      const province = this.provinceList.find((p) => p.code === provinceCode)
      if (province) {
        this.rebateForm.provinceName = province.name
      }
      if (provinceCode) {
        this.loadCityList(provinceCode, 'form')
      } else {
        this.formCityList = []
      }
    },
    handleDialogClose() {
      this.isViewMode = false
      this.isEditMode = false
      this.resetForm()
    },
    async handleSubmit() {
      try {
        await this.$refs.rebateForm.validate()

        // 设置城市名称
        const city = this.formCityList.find(
          (c) => c.code === this.rebateForm.cityCode
        )
        if (city) {
          this.rebateForm.cityName = city.name
        }
        if (!this.rebateForm.provinceName) {
          const province = this.provinceList.find(
            (p) => p.code === this.rebateForm.provinceCode
          )
          if (province) {
            this.rebateForm.provinceName = province.name
          }
        }

        this.submitLoading = true
        let res
        if (this.isEditMode) {
          res = await this.$http.post(
            'rebateRate/updateRebateRate.do',
            this.rebateForm
          )
        } else {
          res = await this.$http.post(
            'rebateRate/addRebateRate.do',
            this.rebateForm
          )
        }

        if (res.code === '0' || res.code === 0) {
          this.$message.success(this.isEditMode ? '修改成功' : '新增成功')
          this.dialogVisible = false
          this.fetchList()
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (error) {
        if (error !== false) {
          console.error('保存返点配置失败:', error)
          this.$message.error('网络请求异常')
        }
      } finally {
        this.submitLoading = false
      }
    },

    // ===== 删除 =====
    handleDelete(row) {
      this.$confirm(
        `确定要删除「${row.provinceName} ${row.cityName}」的返点配置吗？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      )
        .then(async () => {
          try {
            const res = await this.$http.get('rebateRate/deleteRebateRate.do', {
              params: { id: row.id }
            })
            if (res.code === '0' || res.code === 0) {
              this.$message.success('删除成功')
              this.fetchList()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          } catch (error) {
            console.error('删除失败:', error)
            this.$message.error('网络请求异常')
          }
        })
        .catch(() => {})
    }
  },
  filters: {
    formatDate(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const numValue = typeof value === 'number' ? value : Number(value)
        const date = new Date(numValue)
        if (isNaN(date.getTime())) return '-'
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      }
      if (typeof value === 'string') {
        if (/^\d+$/.test(value.trim())) {
          const date = new Date(Number(value))
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}`
          }
        }
        return value.replace('T', ' ').substring(0, 16)
      }
      return '-'
    }
  }
}
</script>

<style scoped>
.rebate-rate-management {
  padding: 20px 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 80px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-left h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.header-desc {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #6b7280;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  padding: 20px;
}

.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.search-label {
  font-size: 14px;
  color: #374151;
}

.search-select {
  width: 140px;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.pagination-info {
  font-size: 13px;
  color: #6b7280;
}

.pagination .el-pagination {
  margin: 0;
}

.pagination .el-pagination button {
  border-radius: 6px;
}

.pagination .el-pagination .el-pager li.active {
  background-color: #22c55e;
  border-color: #22c55e;
  color: #fff;
}

.pagination .el-pagination .el-pager li.active:hover {
  background-color: #16a34a;
}
</style>
